import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";

const NAV = [
  { to: "/shop", label: "Shop" },
  { to: "/bundle", label: "Bundle" },
  { to: "/subscription", label: "Subscription" },
  { to: "/campaigns", label: "Campaigns" },
  { to: "/quiz", label: "Find Yours" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { count, setIsOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-nav py-3" : "py-5 bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2" data-testid="logo-home-link">
          <span className="font-serif text-2xl tracking-tight text-bone">
            Banana<span className="text-gold italic">X</span>
          </span>
          <span className="hidden md:inline-block w-1 h-1 bg-gold rounded-full"></span>
          <span className="hidden md:inline-block text-[10px] tracking-[0.3em] uppercase text-mist">
            Intimate Wellness
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={({ isActive }) =>
                `text-[11px] tracking-[0.22em] uppercase font-light transition-colors ${
                  isActive ? "text-gold" : "text-bone/80 hover:text-gold"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setIsOpen(true)}
            className="relative p-2 text-bone hover:text-gold transition-colors"
            aria-label="Open cart"
            data-testid="open-cart-btn"
          >
            <ShoppingBag className="w-5 h-5 stroke-[1.4]" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-gold text-ink-950 text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
          <button
            className="lg:hidden p-2 text-bone"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="lg:hidden glass-nav border-t border-white/10 px-6 py-6 flex flex-col gap-4"
          data-testid="mobile-nav"
        >
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `text-sm tracking-[0.2em] uppercase ${
                  isActive ? "text-gold" : "text-bone/80"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}
